import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, Save, Trash2, Upload, Palette } from "lucide-react";
import { toast } from "sonner";

export default function BrandBrainPanel({ accent, gradient }) {
  const [profile, setProfile] = useState({ name: "", palette: [], voice: "", description: "", typography: "" });
  const [palette, setPalette] = useState([]);
  const [refs, setRefs] = useState([]);
  const [logo, setLogo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [color, setColor] = useState("#E1306C");

  const load = async () => {
    try {
      const r = await api.get("/creative/brand");
      setProfile({
        name: r.data.name || "",
        voice: r.data.voice || "",
        description: r.data.description || "",
        typography: r.data.typography || "",
        logo_file_id: r.data.logo_file_id,
        reference_file_ids: r.data.reference_file_ids || [],
      });
      setPalette(r.data.palette || []);
    } catch {}
    try {
      const r = await api.get("/uploads/list?kind=brand_ref");
      setRefs(r.data || []);
    } catch {}
    try {
      const r = await api.get("/uploads/list?kind=brand_logo");
      setLogo((r.data || [])[0] || null);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const addColor = () => {
    if (palette.includes(color)) return;
    setPalette([...palette, color]);
  };
  const removeColor = (c) => setPalette(palette.filter((x) => x !== c));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...profile, palette, logo_file_id: logo?.id, reference_file_ids: refs.map((r) => r.id) };
      await api.put("/creative/brand", body);
      toast.success("Brand profile saved — every Creative generation now honours it.");
    } catch (e) { toast.error("Save failed"); }
    setSaving(false);
  };

  const uploadFile = async (e, kind) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("kind", kind);
    const token = localStorage.getItem("sanaa_token");
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads`, {
        method: "POST", body: fd,
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "upload failed");
      if (kind === "brand_logo") setLogo(data);
      else setRefs([data, ...refs]);
      toast.success("Uploaded");
    } catch (err) { toast.error(err.message || "Upload failed"); }
    e.target.value = "";
  };

  const removeRef = async (id) => {
    await api.delete(`/uploads/${id}`).catch(() => {});
    setRefs(refs.filter((r) => r.id !== id));
  };

  return (
    <div className="space-y-5" data-testid="creative-brand-brain">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Brand Brain</div>
        <h2 className="font-serif text-3xl mt-1 flex items-center gap-2"><Palette className="h-6 w-6" /> Identity training</h2>
        <div className="text-xs text-muted-foreground mt-1">Upload your logo, set a palette + voice. Every Creative generation from now on will enforce this identity.</div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-sm border border-border/60 bg-card p-4 space-y-3">
          <Field label="Brand name">
            <input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              data-testid="brand-name" className="input-sel" placeholder="e.g. Sanaa Coffee" />
          </Field>
          <Field label="One-line voice">
            <input value={profile.voice} onChange={(e) => setProfile({ ...profile, voice: e.target.value })}
              data-testid="brand-voice" className="input-sel" placeholder="e.g. confident, warm, editorial" />
          </Field>
          <Field label="Brand essence (2-4 sentences)">
            <textarea value={profile.description} onChange={(e) => setProfile({ ...profile, description: e.target.value })}
              data-testid="brand-description" rows={3}
              className="w-full resize-none rounded-sm border border-border bg-background p-3 text-sm" />
          </Field>
          <Field label="Typography directive">
            <input value={profile.typography} onChange={(e) => setProfile({ ...profile, typography: e.target.value })}
              data-testid="brand-typography" className="input-sel" placeholder="e.g. serif headlines, sans body" />
          </Field>
          <Field label="Palette">
            <div className="flex flex-wrap gap-2">
              {palette.map((c) => (
                <div key={c} className="inline-flex items-center gap-1 rounded-sm border border-border/60 px-1.5 py-0.5">
                  <div className="h-4 w-4 rounded-sm" style={{ background: c }} />
                  <span className="font-mono text-[11px]">{c}</span>
                  <button onClick={() => removeColor(c)} data-testid={`color-rm-${c}`}><Trash2 className="h-3 w-3" /></button>
                </div>
              ))}
              <div className="inline-flex items-center gap-1">
                <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
                  className="h-7 w-9 rounded-sm border border-border bg-background cursor-pointer" data-testid="palette-color" />
                <button onClick={addColor} data-testid="palette-add" className="h-7 px-2 text-xs rounded-sm border border-border hover:bg-secondary">Add</button>
              </div>
            </div>
          </Field>
        </div>

        <div className="rounded-sm border border-border/60 bg-card p-4 space-y-3">
          <Field label="Logo">
            {logo ? (
              <div className="flex items-center gap-3 p-2 border border-border/60 rounded-sm">
                <div className="text-sm flex-1">{logo.original_name}</div>
                <button onClick={() => { setLogo(null); api.delete(`/uploads/${logo.id}`).catch(()=>{}); }} className="text-xs text-muted-foreground hover:text-foreground" data-testid="brand-logo-remove">Remove</button>
              </div>
            ) : (
              <label className="inline-flex items-center gap-2 h-9 px-3 rounded-sm border border-dashed border-border hover:border-foreground/30 cursor-pointer text-xs">
                <Upload className="h-3 w-3" /> Upload logo
                <input type="file" accept="image/*,.svg,.pdf" onChange={(e) => uploadFile(e, "brand_logo")} className="hidden" data-testid="brand-logo-input" />
              </label>
            )}
          </Field>
          <Field label="Reference materials (brand guidelines, mood boards)">
            <label className="inline-flex items-center gap-2 h-9 px-3 rounded-sm border border-dashed border-border hover:border-foreground/30 cursor-pointer text-xs">
              <Upload className="h-3 w-3" /> Add reference
              <input type="file" multiple onChange={(e) => uploadFile(e, "brand_ref")} className="hidden" data-testid="brand-ref-input" />
            </label>
            <div className="mt-3 space-y-1">
              {refs.map((r) => (
                <div key={r.id} className="flex items-center justify-between text-xs px-2 py-1 border border-border/40 rounded-sm">
                  <span className="truncate">{r.original_name}</span>
                  <button onClick={() => removeRef(r.id)} className="text-muted-foreground hover:text-foreground" data-testid={`ref-rm-${r.id}`}><Trash2 className="h-3 w-3" /></button>
                </div>
              ))}
              {refs.length === 0 && <div className="text-xs text-muted-foreground">No references uploaded.</div>}
            </div>
          </Field>
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="brand-save"
        className="h-10 px-4 rounded-sm text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60"
        style={{ background: gradient }}>
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Save brand profile
      </button>

      <style>{`.input-sel{width:100%;height:36px;padding:0 10px;background:hsl(var(--background));border:1px solid hsl(var(--border));border-radius:2px;font-size:13px;color:hsl(var(--foreground));outline:none;}`}</style>
    </div>
  );
}
function Field({ label, children }) {
  return <label className="block"><span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</span><div className="mt-1.5">{children}</div></label>;
}
